import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { locationsAPI, bookingsAPI, pricingAPI, getServerTime } from '../../api/bookings';
import { bruneiToday, bruneiDateOffset } from '../../lib/brunei';
import { GuestIntent } from '../../utils/storage';
import { DEFAULT_SLOTS, getDayOfWeek, isSlotInBreak } from './utils';

export function useLandingPage() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [availability, setAvailability] = useState({});
  const [pricing, setPricing] = useState({});
  const [loading, setLoading] = useState(false);

  const [serverDate, setServerDate] = useState('');
  const [serverTime, setServerTime] = useState('');
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');

  // ---------- derived ----------
  const getBreakInfo = useMemo(() => {
    if (!selectedLocation || !selectedDate) return null;
    const dayOfWeek = getDayOfWeek(selectedDate);
    const oh = selectedLocation.operating_hours?.[dayOfWeek];
    if (!oh?.breaks?.length) return null;
    return oh.breaks;
  }, [selectedLocation, selectedDate]);

  const timeSlots = useMemo(() => {
    if (!selectedLocation || !selectedDate) return DEFAULT_SLOTS;
    const dayOfWeek = getDayOfWeek(selectedDate);
    const oh = selectedLocation.operating_hours?.[dayOfWeek];
    if (!oh || oh.closed) return [];

    const openTime = oh.open || '08:00';
    const closeTime = oh.close || '22:00';
    const breaks = oh.breaks || [];
    const slots = [];
    const [openHour] = openTime.split(':').map(Number);
    let [closeHour] = closeTime.split(':').map(Number);
    if (closeHour === 0) closeHour = 24;

    for (let hour = openHour; hour < closeHour; hour++) {
      const startHour = hour.toString().padStart(2, '0');
      const slotStart = `${startHour}:00`;
      if (isSlotInBreak(slotStart, breaks)) continue;
      const endHour = ((hour + 1) % 24).toString().padStart(2, '0');
      slots.push(`${startHour}:00-${endHour}:00`);
    }
    return slots;
  }, [selectedLocation, selectedDate]);

  const isLocationClosed = useMemo(() => {
    if (!selectedLocation || !selectedDate) return false;
    const dayOfWeek = getDayOfWeek(selectedDate);
    return selectedLocation.operating_hours?.[dayOfWeek]?.closed === true;
  }, [selectedLocation, selectedDate]);

  const getOperatingHoursDisplay = useCallback(() => {
    if (!selectedLocation || !selectedDate) return null;
    const dayOfWeek = getDayOfWeek(selectedDate);
    const oh = selectedLocation.operating_hours?.[dayOfWeek];
    if (!oh || oh.closed) return 'Closed';
    const closeDisplay = oh.close === '00:00' ? '24:00 (Midnight)' : oh.close;
    return `${oh.open} - ${closeDisplay}`;
  }, [selectedLocation, selectedDate]);

  // ---------- effects ----------
  const loadServerTime = useCallback(async () => {
    try {
      const t = await getServerTime();
      setServerDate(t.date);
      setServerTime(t.time.substring(0, 5));
      setMinDate(t.date);
      setSelectedDate(t.date);
      const maxDateObj = new Date(t.date);
      maxDateObj.setMonth(maxDateObj.getMonth() + 1);
      setMaxDate(maxDateObj.toISOString().split('T')[0]);
    } catch (error) {
      console.error('Error loading server time:', error);
      const today = bruneiToday();
      setSelectedDate(today);
      setMinDate(today);
      setMaxDate(bruneiDateOffset(30));
    }
  }, []);

  const loadLocations = useCallback(async () => {
    try {
      const locs = await locationsAPI.getLocations();
      setLocations(locs);
      if (locs.length > 0) setSelectedLocation(locs[0]);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  }, []);

  const loadAvailabilityAndPricing = useCallback(async () => {
    if (!selectedLocation || !selectedDate || timeSlots.length === 0) return;
    setLoading(true);
    try {
      // One request each for the whole day's availability and pricing
      // (one DB query apiece), instead of one request per visible slot.
      let availData = {};
      let pricingData = {};
      try {
        availData = await bookingsAPI.checkAvailabilityBatch(selectedLocation.id, selectedDate);
      } catch (_error) {
        availData = {};
      }
      try {
        pricingData = await pricingAPI.calculateBatch(selectedLocation.id, selectedDate, 'basketball', 1);
      } catch (_error) {
        pricingData = {};
      }
      setAvailability(availData);
      setPricing(pricingData);
    } catch (error) {
      console.error('Error loading availability:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedLocation, selectedDate, timeSlots]);

  useEffect(() => {
    loadServerTime();
    loadLocations();
  }, [loadServerTime, loadLocations]);

  useEffect(() => {
    if (selectedLocation && selectedDate) loadAvailabilityAndPricing();
  }, [selectedLocation, selectedDate, loadAvailabilityAndPricing]);

  // ---------- handlers ----------
  const handleLocationChange = useCallback((locationId) => {
    const location = locations.find((l) => l.id === locationId);
    setSelectedLocation(location);
    setAvailability({});
    setPricing({});
  }, [locations]);

  const isTimeSlotPast = useCallback((timeSlot) => {
    if (!serverDate || !serverTime) return false;
    if (selectedDate > serverDate) return false;
    if (selectedDate < serverDate) return true;
    const slotStartTime = timeSlot.split('-')[0];
    return slotStartTime <= serverTime;
  }, [serverDate, serverTime, selectedDate]);

  const handleBookSlot = useCallback((timeSlot) => {
    if (isTimeSlotPast(timeSlot)) {
      toast.error('This time slot has already passed');
      return;
    }
    const avail = availability[timeSlot];
    const isFullyBooked = avail && avail.available_courts === 0;
    if (isFullyBooked) {
      toast.error('This slot is fully booked. Please pick a different time.');
      return;
    }

    // Always save this selection before leaving the landing page — so
    // BookingFlow picks it up as the freshest intent. This matters even for
    // an already-logged-in visitor (stale session, never logged out): without
    // this, their fresh pick here would be silently discarded in favour of an
    // old WIP snapshot or today's default once /book mounts.
    GuestIntent.set(selectedLocation?.id, selectedDate, timeSlot);

    const isLoggedIn = !!(localStorage.getItem('user') || localStorage.getItem('guest'));
    if (!isLoggedIn) {
      toast.info('Please login to continue booking');
      navigate('/auth');
    } else {
      navigate('/book');
    }
  }, [isTimeSlotPast, availability, navigate, selectedLocation, selectedDate]);

  return {
    navigate,
    locations, selectedLocation, selectedDate, availability, pricing,
    loading, minDate, maxDate,
    setSelectedDate,
    timeSlots, getBreakInfo, isLocationClosed, getOperatingHoursDisplay,
    handleLocationChange, isTimeSlotPast, handleBookSlot,
  };
}
